import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { format } from 'date-fns'

export class FinancialStatementPDF {
  constructor() {
    this.pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    })
    this.pageWidth = this.pdf.internal.pageSize.getWidth()
    this.pageHeight = this.pdf.internal.pageSize.getHeight()
    this.margin = 15
    this.currentY = this.margin
  }

  // Add header with company logo and title
  addHeader(companyName, statementType, reportingPeriod) {
    // Company header
    this.pdf.setFontSize(20)
    this.pdf.setFont('helvetica', 'bold')
    this.pdf.text(companyName || 'UFCC Microfinance', this.pageWidth / 2, this.currentY, { align: 'center' })
    
    this.currentY += 10
    this.pdf.setFontSize(16)
    this.pdf.text(`${this.formatStatementType(statementType)}`, this.pageWidth / 2, this.currentY, { align: 'center' })
    
    this.currentY += 8
    this.pdf.setFontSize(12)
    this.pdf.setFont('helvetica', 'normal')
    this.pdf.text(`For the period ending: ${format(new Date(reportingPeriod), 'MMMM dd, yyyy')}`, 
                  this.pageWidth / 2, this.currentY, { align: 'center' })
    
    this.currentY += 15
    
    // Add a line separator
    this.pdf.setLineWidth(0.5)
    this.pdf.line(this.margin, this.currentY, this.pageWidth - this.margin, this.currentY)
    this.currentY += 10
  }

  // Add customer information section
  addCustomerInfo(customerName, memberId, statementDate) {
    this.pdf.setFontSize(12)
    this.pdf.setFont('helvetica', 'bold')
    this.pdf.text('Customer Information:', this.margin, this.currentY)
    this.currentY += 8

    this.pdf.setFont('helvetica', 'normal')
    this.pdf.text(`Customer Name: ${customerName || 'N/A'}`, this.margin + 5, this.currentY)
    this.currentY += 6
    this.pdf.text(`Member ID: ${memberId || 'N/A'}`, this.margin + 5, this.currentY)
    this.currentY += 6
    this.pdf.text(`Statement Date: ${format(new Date(statementDate), 'MMMM dd, yyyy')}`, this.margin + 5, this.currentY)
    this.currentY += 12
  }

  // Add financial data table
  addFinancialTable(title, data) {
    this.checkPageBreak(40)
    
    this.pdf.setFontSize(14)
    this.pdf.setFont('helvetica', 'bold')
    this.pdf.text(title, this.margin, this.currentY)
    this.currentY += 10

    if (!data || Object.keys(data).length === 0) {
      this.pdf.setFont('helvetica', 'italic')
      this.pdf.text('No data available', this.margin + 5, this.currentY)
      this.currentY += 10
      return
    }

    // Table headers
    const colWidth = (this.pageWidth - 2 * this.margin) / 2
    this.pdf.setFont('helvetica', 'bold')
    this.pdf.setFillColor(240, 240, 240)
    this.pdf.rect(this.margin, this.currentY - 5, colWidth, 8, 'F')
    this.pdf.rect(this.margin + colWidth, this.currentY - 5, colWidth, 8, 'F')
    
    this.pdf.text('Description', this.margin + 2, this.currentY)
    this.pdf.text('Amount (UGX)', this.margin + colWidth + 2, this.currentY)
    this.currentY += 8

    // Table rows
    this.pdf.setFont('helvetica', 'normal')
    Object.entries(data).forEach(([key, value]) => {
      this.checkPageBreak(8)
      
      // Format the key to be more readable
      const formattedKey = this.formatTableKey(key)
      const formattedValue = this.formatCurrency(value)
      
      this.pdf.text(formattedKey, this.margin + 2, this.currentY)
      this.pdf.text(formattedValue, this.margin + colWidth + 2, this.currentY)
      this.currentY += 6
    })
    
    this.currentY += 5
  }

  // Add profit and loss statement
  addProfitLossStatement(data) {
    if (!data) return

    // Map the flat structure to categorized data
    const revenueData = {}
    const expenseData = {}
    const otherData = {}

    Object.entries(data).forEach(([key, value]) => {
      if (typeof value === 'string' && (value === '' || value === null)) return
      
      const numValue = parseFloat(value) || 0
      if (numValue === 0) return

      const lowerKey = key.toLowerCase()

      // Categorize based on key names - fixed logic
      if (lowerKey.includes('revenue') || lowerKey === 'total_revenue') {
        revenueData[key] = numValue
      } else if (lowerKey.includes('expense') || lowerKey.includes('cost') || lowerKey.includes('cogs')) {
        expenseData[key] = numValue
      } else if (lowerKey.includes('gross_profit') || lowerKey.includes('operating_income') || 
                 lowerKey.includes('net_income') || lowerKey === 'other_income') {
        otherData[key] = numValue
      } else if (!key.includes('period') && !key.includes('notes')) {
        otherData[key] = numValue
      }
    })

    // Add revenue section
    if (Object.keys(revenueData).length > 0) {
      this.addFinancialTable('REVENUE', revenueData)
    }

    // Add expense section
    if (Object.keys(expenseData).length > 0) {
      this.addFinancialTable('EXPENSES', expenseData)
    }

    // Add other financial data
    if (Object.keys(otherData).length > 0) {
      this.addFinancialTable('OTHER FINANCIAL DATA', otherData)
    }
    
    // Calculate totals - fixed calculation logic
    const totalRevenue = this.calculateTotal(revenueData)
    const totalExpenses = this.calculateTotal(expenseData)
    
    // Net income should be calculated from operating income if available, 
    // otherwise from revenue minus expenses
    let netIncome
    if (data.operating_income) {
      netIncome = parseFloat(data.operating_income) || 0
    } else if (data.net_income) {
      netIncome = parseFloat(data.net_income) || 0
    } else {
      // Fallback calculation: Revenue - Total Expenses
      netIncome = totalRevenue - totalExpenses
    }

    this.checkPageBreak(25)
    
    // Summary section
    this.pdf.setFont('helvetica', 'bold')
    this.pdf.text('SUMMARY:', this.margin, this.currentY)
    this.currentY += 10

    const summaryData = {
      'Total Revenue': totalRevenue,
      'Total Expenses': totalExpenses,
      'Net Income': netIncome
    }

    Object.entries(summaryData).forEach(([key, value]) => {
      const color = key === 'Net Income' ? (value >= 0 ? [0, 100, 0] : [255, 0, 0]) : [0, 0, 0]
      this.pdf.setTextColor(...color)
      this.pdf.text(`${key}: ${this.formatCurrency(value)}`, this.margin + 5, this.currentY)
      this.currentY += 6
    })
    
    this.pdf.setTextColor(0, 0, 0) // Reset color
  }

  // Add balance sheet
  addBalanceSheet(data) {
    if (!data) return

    // Map the flat structure to categorized data for balance sheet
    const assetsData = {}
    const liabilitiesData = {}
    const equityData = {}
    const otherData = {}

    Object.entries(data).forEach(([key, value]) => {
      if (typeof value === 'string' && (value === '' || value === null)) return
      
      const numValue = parseFloat(value) || 0
      if (numValue === 0) return
      const lowerKey = key.toLowerCase()

      // Categorize based on key names for balance sheet
      if (lowerKey.includes('asset') || lowerKey.includes('cash') || lowerKey.includes('inventory') || lowerKey.includes('receivable')) {
        assetsData[key] = numValue
      } else if (lowerKey.includes('liability') || lowerKey.includes('payable') || lowerKey.includes('debt') || lowerKey.includes('loan')) {
        liabilitiesData[key] = numValue
      } else if (lowerKey.includes('equity') || lowerKey.includes('capital') || lowerKey.includes('retained')) {
        equityData[key] = numValue
      } else if (!key.includes('period') && !key.includes('notes')) {
        otherData[key] = numValue
      }
    })

    // Add sections if they have data
    if (Object.keys(assetsData).length > 0) {
      this.addFinancialTable('ASSETS', assetsData)
    }
    
    if (Object.keys(liabilitiesData).length > 0) {
      this.addFinancialTable('LIABILITIES', liabilitiesData)
    }
    
    if (Object.keys(equityData).length > 0) {
      this.addFinancialTable('EQUITY', equityData)
    }

    // Add other data that doesn't fit standard categories
    if (Object.keys(otherData).length > 0) {
      this.addFinancialTable('OTHER FINANCIAL DATA', otherData)
    }
    
    // Calculate totals
    const totalAssets = this.calculateTotal(assetsData)
    const totalLiabilities = this.calculateTotal(liabilitiesData)
    const totalEquity = this.calculateTotal(equityData)

    this.checkPageBreak(25)
    
    // Summary section
    this.pdf.setFont('helvetica', 'bold')
    this.pdf.text('SUMMARY:', this.margin, this.currentY)
    this.currentY += 10

    const summaryData = {
      'Total Assets': totalAssets,
      'Total Liabilities': totalLiabilities,
      'Total Equity': totalEquity,
      'Balance Check': totalAssets - (totalLiabilities + totalEquity)
    }

    Object.entries(summaryData).forEach(([key, value]) => {
      const color = key === 'Balance Check' ? (Math.abs(value) < 0.01 ? [0, 100, 0] : [255, 0, 0]) : [0, 0, 0]
      this.pdf.setTextColor(...color)
      this.pdf.text(`${key}: ${this.formatCurrency(value)}`, this.margin + 5, this.currentY)
      this.currentY += 6
    })
    
    this.pdf.setTextColor(0, 0, 0) // Reset color
  }

  // Add footer
  addFooter() {
    const footerY = this.pageHeight - 20
    this.pdf.setFontSize(10)
    this.pdf.setFont('helvetica', 'italic')
    this.pdf.text(`Generated on ${format(new Date(), 'MMMM dd, yyyy HH:mm')}`, 
                  this.margin, footerY)
    this.pdf.text('UFCC Microfinance System', 
                  this.pageWidth - this.margin, footerY, { align: 'right' })
    
    // Page number
    this.pdf.text(`Page ${this.pdf.internal.getCurrentPageInfo().pageNumber}`, 
                  this.pageWidth / 2, footerY, { align: 'center' })
  }

  // Utility methods
  formatStatementType(type) {
    const types = {
      'profit_loss': 'Profit & Loss Statement',
      'balance_sheet': 'Balance Sheet',
      'cash_flow': 'Cash Flow Statement',
      'cumulative': 'Cumulative Financial Report'
    }
    return types[type] || 'Financial Statement'
  }

  formatTableKey(key) {
    return key.replace(/_/g, ' ')
             .replace(/\b\w/g, l => l.toUpperCase())
  }

  formatCurrency(amount) {
    if (typeof amount !== 'number') return 'UGX 0.00'
    return `UGX ${amount.toLocaleString('en-UG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  calculateTotal(data) {
    return Object.values(data).reduce((sum, value) => {
      const numValue = parseFloat(value) || 0
      return sum + numValue
    }, 0)
  }

  checkPageBreak(requiredSpace) {
    if (this.currentY + requiredSpace > this.pageHeight - 30) {
      this.pdf.addPage()
      this.currentY = this.margin
    }
  }

  // Generate and download PDF
  async generatePDF(statementData) {
    try {
      const {
        customerName,
        memberId,
        statementType,
        reportingPeriod,
        createdAt,
        financialData,
        companyName
      } = statementData

      // Add header
      this.addHeader(companyName, statementType, reportingPeriod)
      
      // Add customer info
      this.addCustomerInfo(customerName, memberId, createdAt)

      // Add financial data based on statement type
      if (statementType === 'profit_loss') {
        this.addProfitLossStatement(financialData)
      } else if (statementType === 'balance_sheet') {
        this.addBalanceSheet(financialData)
      } else {
        // Generic financial data display
        this.addFinancialTable('Financial Data', financialData)
      }

      // Add footer
      this.addFooter()

      // Generate filename
      const filename = `${statementType}_${customerName || 'unknown'}_${format(new Date(reportingPeriod), 'yyyy-MM-dd')}.pdf`
      
      // Save PDF
      this.pdf.save(filename)
      
      return { success: true, filename }
    } catch (error) {
      console.error('PDF generation failed:', error)
      return { success: false, error: error.message }
    }
  }
}

// Utility function to generate PDF from statement data
export const generateFinancialStatementPDF = async (statementData) => {
  const pdfGenerator = new FinancialStatementPDF()
  return await pdfGenerator.generatePDF(statementData)
}

// Cumulative report PDF — `report` is the `data` object returned by the
// GET-CUMULATIVE-FINANCIAL-REPORT endpoint.
export const generateCumulativeReportPDF = async (report, { customerName, memberId, companyName } = {}) => {
  try {
    const gen = new FinancialStatementPDF()
    const pl = report?.profit_loss || {}
    const totals = pl.totals || {}
    const endDate = report?.end_date || format(new Date(), 'yyyy-MM-dd')
    const startLabel = report?.start_date || 'inception'

    gen.addHeader(companyName || 'UFCC Microfinance', 'cumulative', endDate)
    gen.addCustomerInfo(customerName, memberId || report?.mem_no, new Date().toISOString())

    gen.pdf.setFontSize(11)
    gen.pdf.setFont('helvetica', 'normal')
    gen.pdf.text(
      `Cumulative period: ${startLabel} to ${endDate} (${pl.statements_count || 0} P&L statement(s) included)`,
      gen.margin, gen.currentY
    )
    gen.currentY += 10

    const products = pl.products || []
    if (products.length > 0) {
      gen.addFinancialTable('REVENUE BY PRODUCT', Object.fromEntries(
        products.map((p) => [p.product_name || 'Unnamed product', Number(p.revenue) || 0])
      ))
    }

    const expenses = pl.expenses || []
    if (expenses.length > 0) {
      gen.addFinancialTable('OPERATING EXPENSES BY CATEGORY', Object.fromEntries(
        expenses.map((e) => [e.category || 'Uncategorised', Number(e.amount) || 0])
      ))
    }

    gen.addFinancialTable('PROFIT & LOSS SUMMARY (CUMULATIVE)', {
      total_revenue: Number(totals.total_revenue) || 0,
      total_cost_of_goods_sold: Number(totals.total_cogs) || 0,
      gross_profit: Number(totals.gross_profit) || 0,
      total_operating_expenses: Number(totals.total_operating_expenses) || 0,
      other_income: Number(totals.other_income) || 0,
      other_expenses: Number(totals.other_expenses) || 0,
      net_profit: Number(totals.net_income) || 0
    })

    const bs = report?.balance_sheet
    if (bs?.totals) {
      gen.addFinancialTable(`BALANCE SHEET AS AT ${bs.as_of || endDate}`, {
        total_current_assets: Number(bs.totals.total_current_assets) || 0,
        net_fixed_assets: Number(bs.totals.net_fixed_assets) || 0,
        total_assets: Number(bs.totals.total_assets) || 0,
        total_current_liabilities: Number(bs.totals.total_current_liabilities) || 0,
        total_liabilities: Number(bs.totals.total_liabilities) || 0,
        total_equity: Number(bs.totals.total_equity) || 0
      })
    }

    gen.addFooter()
    const filename = `cumulative_report_${(customerName || report?.mem_no || 'member').toString().replace(/\s+/g, '_')}_${endDate}.pdf`
    gen.pdf.save(filename)
    return { success: true, filename }
  } catch (error) {
    console.error('Cumulative report PDF generation failed:', error)
    return { success: false, error: error.message }
  }
}

// Function to capture HTML element as PDF
export const generatePDFFromElement = async (elementId, filename = 'document.pdf') => {
  try {
    const element = document.getElementById(elementId)
    if (!element) {
      throw new Error('Element not found')
    }

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: false
    })

    const imgData = canvas.toDataURL('image/png')
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    })

    const pdfWidth = pdf.internal.pageSize.getWidth()
    const pdfHeight = pdf.internal.pageSize.getHeight()
    const imgWidth = canvas.width
    const imgHeight = canvas.height
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight)
    const imgX = (pdfWidth - imgWidth * ratio) / 2
    const imgY = 15

    pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio)
    pdf.save(filename)

    return { success: true, filename }
  } catch (error) {
    console.error('PDF generation from element failed:', error)
    return { success: false, error: error.message }
  }
}
